import React from "react";
import { useTranslation } from "react-i18next";
import StatusBadge from "../../StatusBadge";
import { Tenant, TenantStatus, getTenantStatusKey } from "../../../services/tenantsService";
import {
  ContentContainer,
  TitleRow,
  ModalTitle,
  ModalSubtitle,
  SectionDivider,
  InfoGrid,
  InfoItem,
  InfoItemFull,
  InfoLabel,
  InfoValue,
  ButtonsContainer,
  CloseButton,
  EditButton,
} from "./ViewTenantModal.styled";

const getTenantStatusLabel = (
  status: TenantStatus,
  t: (k: string, f: string) => string,
): string => {
  switch (status) {
    case TenantStatus.Active:
      return t("tenantManagement.statuses.active", "Active");
    case TenantStatus.Invited:
      return t("tenantManagement.statuses.pendingActivation", "Pending");
    case TenantStatus.Suspended:
      return t("tenantManagement.statuses.suspended", "Suspended");
    case TenantStatus.Inactive:
      return t("tenantManagement.statuses.inactive", "Inactive");
    default:
      return t("tenantManagement.statuses.unknown", "Unknown");
  }
};

interface ViewTenantModalContentProps {
  tenant: Tenant;
  onClose: () => void;
  onEdit: () => void;
}

const ViewTenantModalContent: React.FC<ViewTenantModalContentProps> = ({
  tenant,
  onClose,
  onEdit,
}) => {
  const { t } = useTranslation();

  return (
    <ContentContainer>
      <TitleRow>
        <ModalTitle>{tenant.clinicName}</ModalTitle>
        <StatusBadge
          status={getTenantStatusKey(tenant.status)}
          label={getTenantStatusLabel(tenant.status, t)}
          width={90}
        />
      </TitleRow>
      <ModalSubtitle>
        {t("tenantManagement.viewTenant.subtitle", "Tenant details")}
      </ModalSubtitle>

      <InfoGrid>
        <InfoItem>
          <InfoLabel>
            {t("tenantManagement.columns.tenantId", "Tenant ID")}
          </InfoLabel>
          <InfoValue>{tenant.id}</InfoValue>
        </InfoItem>

        <InfoItem>
          <InfoLabel>
            {t("tenantManagement.columns.createdDate", "Created Date")}
          </InfoLabel>
          <InfoValue>{tenant.createdDate}</InfoValue>
        </InfoItem>

        <InfoItem>
          <InfoLabel>
            {t("tenantManagement.columns.customerKey", "Customer Key")}
          </InfoLabel>
          <InfoValue>{tenant.customerKey}</InfoValue>
        </InfoItem>

        <InfoItem>
          <InfoLabel>
            {t("tenantManagement.columns.lastUpdated", "Last Updated")}
          </InfoLabel>
          <InfoValue>{tenant.lastUpdated}</InfoValue>
        </InfoItem>
      </InfoGrid>

      <SectionDivider />

      <InfoGrid>
        <InfoItem>
          <InfoLabel>
            {t(
              "tenantManagement.viewTenant.contactName",
              "Main Contact Person Name",
            )}
          </InfoLabel>
          <InfoValue>{tenant.contactName}</InfoValue>
        </InfoItem>

        <InfoItem>
          <InfoLabel>
            {t("tenantManagement.viewTenant.phone", "Phone")}
          </InfoLabel>
          <InfoValue>{tenant.phoneNumber}</InfoValue>
        </InfoItem>

        <InfoItemFull>
          <InfoLabel>
            {t(
              "tenantManagement.viewTenant.contactEmail",
              "Main Contact Person Email",
            )}
          </InfoLabel>
          <InfoValue>{tenant.contactEmail}</InfoValue>
        </InfoItemFull>
      </InfoGrid>

      <ButtonsContainer>
        <CloseButton variant="outlined" onClick={onClose}>
          {t("common.close", "Close")}
        </CloseButton>
        <EditButton variant="contained" onClick={onEdit}>
          {t("tenantManagement.actions.editTenant", "Edit Tenant")}
        </EditButton>
      </ButtonsContainer>
    </ContentContainer>
  );
};

export { ViewTenantModalContent };
export default ViewTenantModalContent;
